import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  AlertTitle,
  Button,
  Divider,
  MenuItem,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { debounce } from '@mui/material/utils';
import {
  BenchmarkGrid,
  BenchmarkGridType,
  KPI_METRIC_ALREADY_ASSIGNED_ERROR,
  KPI_METRIC_INELIGIBLE_ERROR,
  zBenchmarkGridType,
  zBenchmarkUpdateType,
} from '@strength/domain';
import { I18nTranslator } from '@strength/i18n';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../../hooks/useToast';
import { z } from 'zod';
import { trpc } from '../../../../trpc/react';
import { Drawer, DrawerFooter, DrawerHeader } from '../../../components/drawer';
import {
  Form,
  FormField,
  FormItem,
  FormMessage,
} from '../../../components/form/form';
import { LabeledRadioChip } from '../../../components/input/labeled-radio-chip';
import { MultiSelect } from '../../../components/input/multi-select';
import { DeleteConfirmationModal } from '../../../components/modals/delete-confirmation-modal';
import { cn } from '../../../components/utils';
import { LibraryExercise } from '../../library/exercises/types';
import { useMetrics } from '../../../hooks/useMetrics';

export const buildBenchmarksFormSchema = (t: I18nTranslator) =>
  z
    .object({
      name: z
        .string()
        .min(1, t('benchmarkNameMin', { count: 1 }))
        .max(10, t('benchmarkNameMax', { count: 10 })),
      type: zBenchmarkGridType,
      repCount: z.number(),
      coreExerciseIds: z.array(z.string()),
      metricIds: z.array(z.string()),
      updateType: zBenchmarkUpdateType,
    })
    // Rep-max grids require core exercises and a positive rep count. KPI grids
    // use metrics instead, carry no rep count, and accept an empty metric set
    // (the server allows it), so those rep-only constraints are skipped.
    .superRefine((value, ctx) => {
      if (value.type === 'rep') {
        if (value.coreExerciseIds.length < 1) {
          ctx.addIssue({
            code: 'custom',
            path: ['coreExerciseIds'],
            message: t('benchmarkCoreExercisesRequired'),
          });
        }
        if (!(value.repCount > 0)) {
          ctx.addIssue({
            code: 'custom',
            path: ['repCount'],
            message: t('benchmarkRepCountRequired'),
          });
        }
      }
    });
export type BenchmarksForm = z.infer<
  ReturnType<typeof buildBenchmarksFormSchema>
>;

/**
 * Which fields the drawer renders for a given benchmark type. KPI grids replace
 * core exercises with a metrics multi-select and drop the rep count + update
 * fields entirely. The type selector, and KPI mode itself, only exist when the
 * KPI flag is on: with the flag off the drawer behaves exactly as before
 * (rep-only), so a grid is implicitly a rep max.
 */
export const getBenchmarkFieldVisibility = ({
  type,
  kpiEnabled,
}: {
  type: BenchmarkGridType;
  kpiEnabled: boolean;
}) => {
  const isKpi = kpiEnabled && type === 'kpi';
  return {
    showTypeSelector: kpiEnabled,
    isKpi,
    showCoreExercises: !isKpi,
    showMetrics: isKpi,
    showRepNumber: !isKpi,
    showUpdates: !isKpi,
  };
};

/**
 * Maps a failed create/edit mutation to the inline message shown on the metrics
 * field. Handles the router's one-metric-per-team error and, defensively, the
 * ineligible-metric error (which should not be reachable through the UI). Any
 * other failure falls through to the global handler.
 */
export const getKpiMetricsInlineError = (
  error: unknown,
  t: I18nTranslator,
): string | undefined => {
  if (!(error instanceof Error)) {
    return undefined;
  }
  if (error.message === KPI_METRIC_ALREADY_ASSIGNED_ERROR) {
    return t('benchmarkKpiMetricAlreadyAssigned');
  }
  if (error.message === KPI_METRIC_INELIGIBLE_ERROR) {
    return t('benchmarkKpiMetricIneligible');
  }
  return undefined;
};

type KpiMetricOption = { id: string; name: string };

/**
 * Builds the metrics multi-select options for a KPI grid. Options are the
 * currently-eligible metrics (available to add) unioned with the grid's
 * already-assigned metrics, so a metric that is still assigned but no longer
 * eligible keeps rendering as a selected chip and is preserved on save.
 * Deduped by id, eligible metrics first.
 */
export const buildKpiMetricOptions = (
  eligibleMetrics: KpiMetricOption[],
  gridMetrics: KpiMetricOption[],
): KpiMetricOption[] => {
  const byId = new Map<string, KpiMetricOption>();
  for (const metric of [...eligibleMetrics, ...gridMetrics]) {
    if (!byId.has(metric.id)) {
      byId.set(metric.id, { id: metric.id, name: metric.name });
    }
  }
  return [...byId.values()];
};

/**
 * Names of the currently-selected metrics that are NOT configured for
 * prescription (their metric config `isPercent` is falsy, covering both an
 * explicit `false` and an omitted flag). Drives the drawer's non-blocking
 * warning; this is presentation only and never affects whether a benchmark can
 * be created. Reads configs already loaded into the drawer via `getMetric`.
 */
export const getNonPrescriptionMetricNames = (
  metricIds: string[],
  getMetric: (id: string) => { name: string; config: { isPercent?: boolean } },
): string[] => {
  const names: string[] = [];
  for (const id of metricIds) {
    let metric: { name: string; config: { isPercent?: boolean } };
    try {
      metric = getMetric(id);
    } catch {
      // A selected id that no longer resolves to a loaded metric (e.g. a stale
      // grid assignment) has nothing to warn about; skip it rather than crash
      // this presentation-only warning.
      continue;
    }
    // `useMetrics` hands back a nameless placeholder metric whenever its query
    // holds no data, so listing it would render blank names in the warning.
    if (!metric.name) {
      continue;
    }
    if (!metric.config.isPercent) {
      names.push(metric.name);
    }
  }
  return names;
};

export const BenchmarksDrawer = ({
  grid,
  teamName,
  teamId,
  allExercises,
  open,
  onClose,
  onSubmit,
  onDelete,
  canEdit = true,
}: {
  grid?: BenchmarkGrid;
  teamName: string;
  teamId: string;
  allExercises: LibraryExercise[];
  open: boolean;
  onClose: () => void;
  onSubmit: (
    benchmarkForm: BenchmarksForm,
    gridId?: string,
  ) => Promise<unknown>;
  onDelete: (gridId: string) => void;
  /** Whether the viewer holds `teams.benchmarks.edit`. */
  canEdit?: boolean;
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const isEdit = !!grid;
  const title = isEdit ? t('benchmarkMetricEdit') : t('benchmarkMetricNew');

  const form = useForm<BenchmarksForm>({
    resolver: zodResolver(buildBenchmarksFormSchema(t)),
    defaultValues: getBenchmarksForm(grid),
    mode: 'onChange',
  });
  const { handleSubmit, control } = form;

  const [repCount, type, name] = form.watch(['repCount', 'type', 'name']);
  const [debouncedName, setDebouncedName] = useState(name);
  const [metricsError, setMetricsError] = useState<string | undefined>(
    undefined,
  );

  const { showTypeSelector, isKpi, showCoreExercises, showMetrics } =
    getBenchmarkFieldVisibility({ type, kpiEnabled: true });

  const isKpiEdit = isEdit && grid?.type === 'kpi';

  // Metric configs are already loaded into the drawer; `getMetric` exposes each
  // metric's `config.isPercent` (the prescription-enabled flag). We use it to
  // warn about selected metrics that can't be prescribed. Presentation only:
  // this never gates creation.
  const { getMetric, isLoading: metricsLoading } = useMetrics();
  const selectedMetricIds = form.watch('metricIds');
  const [prescriptionWarningDismissed, setPrescriptionWarningDismissed] =
    useState(false);
  const nonPrescriptionMetricNames = useMemo(
    () =>
      showMetrics && !metricsLoading
        ? getNonPrescriptionMetricNames(selectedMetricIds, getMetric)
        : [],
    [showMetrics, metricsLoading, selectedMetricIds, getMetric],
  );
  const showPrescriptionWarning =
    nonPrescriptionMetricNames.length > 0 && !prescriptionWarningDismissed;

  // Metrics eligible to add for this team, keeping the edited grid's own
  // metrics selectable via `excludeGridId`.
  const eligibleMetricsQuery = trpc.benchmarks.eligibleKpiMetrics.useQuery(
    { excludeGridId: grid?.id },
    {},
  );
  // Source of truth for an existing KPI grid's currently-assigned metrics,
  // including any that have since become ineligible. Used to preselect and to
  // keep those metrics as options so they are preserved on save.
  const gridMetricsQuery = trpc.benchmarks.kpiGridMetrics.useQuery(
    { gridId: grid?.id ?? '' },
    { enabled: isKpiEdit },
  );

  const metricOptions = useMemo(
    () =>
      buildKpiMetricOptions(
        eligibleMetricsQuery.data ?? [],
        gridMetricsQuery.data ?? [],
      ),
    [eligibleMetricsQuery.data, gridMetricsQuery.data],
  );

  // On opening the drawer for an existing KPI grid, preselect its current
  // metrics once the grid-metrics query has resolved.
  const [metricsInitialized, setMetricsInitialized] = useState(false);
  useEffect(() => {
    if (!metricsInitialized && isKpiEdit && gridMetricsQuery.data) {
      form.setValue(
        'metricIds',
        gridMetricsQuery.data.map((metric) => metric.id),
      );
      setMetricsInitialized(true);
    }
  }, [metricsInitialized, isKpiEdit, gridMetricsQuery.data, form]);

  const metricsErrorId = 'benchmark-metrics-error';

  // Debounce the name field using MUI's debounce utility
  const debouncedSetDebouncedName = useMemo(
    () => debounce((value: string) => setDebouncedName(value), 600),
    [],
  );

  const handleClose = useCallback(() => {
    debouncedSetDebouncedName.clear();
    setDebouncedName('');
    setMetricsError(undefined);
    setMetricsInitialized(false);
    // Re-show the warning next time the drawer opens while non-prescription
    // metrics are still selected: dismissal only lasts the current session.
    setPrescriptionWarningDismissed(false);
    form.reset();
    onClose();
  }, [form, onClose, debouncedSetDebouncedName]);

  const onSubmitHandler = async (form: BenchmarksForm) => {
    setMetricsError(undefined);
    try {
      await onSubmit(form, grid?.id);
      handleClose();
    } catch (error) {
      // Surface the one-metric-per-team conflict inline on the metrics field.
      // For any other failure (network/server), keep the drawer open and show a
      // generic error so the coach's input isn't lost to a silent close.
      const inlineError = getKpiMetricsInlineError(error, t);
      if (inlineError) {
        setMetricsError(inlineError);
      } else {
        toast({
          title: t('error'),
          description: t('somethingWentWrong'),
          severity: 'error',
        });
      }
    }
  };

  const canBeDeletedQuery = trpc.benchmarks.canBeDeleted.useQuery(
    {
      id: grid?.id ?? '',
    },
    {
      enabled: !!grid?.id,
    },
  );
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const onDeleteHandler = useCallback(() => {
    if (!grid?.id || !canBeDeletedQuery.data?.deletable) {
      return;
    }
    if (canBeDeletedQuery.data.hasRecordedValues) {
      setDeleteConfirmOpen(true);
      return;
    }
    onDelete(grid.id);
    handleClose();
  }, [grid?.id, onDelete, handleClose, canBeDeletedQuery.data]);
  const onConfirmDelete = useCallback(() => {
    if (!grid?.id) {
      return;
    }
    onDelete(grid.id);
    setDeleteConfirmOpen(false);
    handleClose();
  }, [grid?.id, onDelete, handleClose]);
  const deleteTooltip = canBeDeletedQuery.data?.deletable
    ? ''
    : t('benchmarkGridInUseCannotBeDeleted');

  const hasGridWithNameQuery = trpc.benchmarks.hasGridWithName.useQuery(
    {
      name: debouncedName,
      excludeId: isEdit ? grid?.id : undefined,
    },
    {
      enabled: !!debouncedName,
    },
  );

  const isGridDuplicate = hasGridWithNameQuery.data ?? false;

  return (
    <Drawer anchor="right" open={open} onClose={handleClose}>
      <DrawerHeader onClose={handleClose}>
        <Typography variant="h2">{title}</Typography>
        <Typography variant="body2" color="text.secondary" className="ml-0.5">
          {teamName}
        </Typography>
      </DrawerHeader>
      <Form {...form}>
        <form
          id="benchmarks-form"
          onSubmit={handleSubmit(onSubmitHandler)}
          className="flex flex-col gap-2 flex-grow"
        >
          <div className="flex flex-col gap-2 grow p-6 pt-0">
            {showPrescriptionWarning && (
              <Alert
                severity="warning"
                onClose={() => setPrescriptionWarningDismissed(true)}
                closeText={t('dismiss')}
              >
                <AlertTitle>
                  {t('benchmarkPrescriptionWarningTitle')}
                </AlertTitle>
                {t('benchmarkPrescriptionWarningBody', {
                  names: nonPrescriptionMetricNames.join(', '),
                })}
              </Alert>
            )}
            {isGridDuplicate && (
              <Alert severity="error">
                <AlertTitle>
                  {t('benchmarkNameMustBeUnique', {
                    name: debouncedName,
                  })}
                </AlertTitle>
              </Alert>
            )}
            <FormField
              control={control}
              name="name"
              render={({ field, fieldState }) => (
                <FormItem>
                  <TextField
                    label={t('name')}
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      debouncedSetDebouncedName(e.target.value);
                    }}
                    error={!!fieldState.error}
                    placeholder={t('enterName')}
                    fullWidth
                  />
                  {/* buildBenchmarksFormSchema bakes the fully-translated
                  message into the zod issue (it needs the `{{count}}`
                  interpolation), so render it directly instead of letting
                  FormMessage re-translate it as a lookup key. */}
                  <FormMessage>{fieldState.error?.message}</FormMessage>
                </FormItem>
              )}
            />
            {showTypeSelector && (
              <FormField
                control={control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <TextField
                      label={t('type')}
                      select
                      {...field}
                      // Editing cannot change a grid's type: rep and KPI grids
                      // have different data shapes and the server does not allow
                      // switching an existing grid between them.
                      disabled={isEdit}
                      fullWidth
                    >
                      <MenuItem value="rep">{t('benchmarkTypeRep')}</MenuItem>
                      <MenuItem value="kpi">{t('benchmarkTypeKpi')}</MenuItem>
                    </TextField>
                  </FormItem>
                )}
              />
            )}
            {showCoreExercises && (
              <FormField
                control={control}
                name="coreExerciseIds"
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <MultiSelect
                      label={t('coreExercises')}
                      placeholder={t('all')}
                      selectedOptions={allExercises.filter((e) =>
                        value.includes(e.id),
                      )}
                      options={allExercises}
                      onChange={(selected) =>
                        onChange(selected.map((option) => option.id))
                      }
                      optionToName={(option) => option.name}
                      fullWidth
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            {showMetrics && (
              <FormField
                control={control}
                name="metricIds"
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <MultiSelect
                      label={t('metrics')}
                      placeholder={t('selectMetrics')}
                      selectedOptions={metricOptions.filter((metric) =>
                        value.includes(metric.id),
                      )}
                      options={metricOptions}
                      onChange={(selected) => {
                        // Clear a stale duplicate/ineligible error as soon as the
                        // selection changes, rather than waiting for the next save.
                        if (metricsError) {
                          setMetricsError(undefined);
                        }
                        onChange(selected.map((option) => option.id));
                      }}
                      optionToName={(option) => option.name}
                      error={!!metricsError}
                      describedById={metricsError ? metricsErrorId : undefined}
                      fullWidth
                    />
                    {metricsError && (
                      <Typography
                        id={metricsErrorId}
                        role="alert"
                        variant="body2"
                        color="error"
                        className="ml-1"
                      >
                        {metricsError}
                      </Typography>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            {!isKpi && (
              <FormField
                control={control}
                name="repCount"
                render={({ field: { onChange, value, ...field } }) => (
                  <FormItem>
                    <TextField
                      label={t('repNumber')}
                      type="number"
                      inputMode="numeric"
                      {...field}
                      value={value || 0}
                      disabled={isEdit}
                      onChange={(e) => {
                        const parsed = parseInt(e.target.value, 10);
                        onChange(Number.isNaN(parsed) ? 0 : parsed);
                      }}
                      placeholder={t('repNumber')}
                      fullWidth
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {!isKpi && (
              <>
                <Divider className="my-2" />
                <Typography
                  variant="h4"
                  color="text.secondary"
                  className="ml-1"
                >
                  {t('updates')}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  className="ml-1"
                >
                  {t('updateTypeDescription')}
                </Typography>
                <FormField
                  control={control}
                  name="updateType"
                  render={({ field }) => (
                    <FormItem>
                      <RadioGroup
                        row
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        sx={{ gap: 1 }}
                      >
                        <LabeledRadioChip value="tested" label={t('tested')} />
                        <LabeledRadioChip value="none" label={t('noUpdates')} />
                      </RadioGroup>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}
          </div>
        </form>
      </Form>
      <DrawerFooter
        className={cn(
          isEdit ? 'justify-between' : 'justify-end',
          'flex flex-row gap-2 w-full p-8',
        )}
      >
        {isEdit && (
          <Tooltip title={deleteTooltip} arrow>
            <span>
              <Button
                variant="containedDestructive"
                onClick={onDeleteHandler}
                disabled={
                  !canEdit ||
                  canBeDeletedQuery.isLoading ||
                  !canBeDeletedQuery.data?.deletable
                }
              >
                {t('delete')}
              </Button>
            </span>
          </Tooltip>
        )}
        {grid?.id && (
          <DeleteConfirmationModal
            id={grid.id}
            open={deleteConfirmOpen}
            title={t('deleteBenchmark')}
            message={t('deleteBenchmarkMessage')}
            onDelete={onConfirmDelete}
            onClose={() => setDeleteConfirmOpen(false)}
          />
        )}
        <div className="flex flex-row gap-2">
          <Button variant="outlined" onClick={handleClose}>
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            form="benchmarks-form"
            disabled={
              !form.formState.isValid ||
              isGridDuplicate ||
              // Rep grids require a rep count; KPI grids have none.
              (!isKpi && !repCount)
            }
          >
            {t('save')}
          </Button>
        </div>
      </DrawerFooter>
    </Drawer>
  );
};

const getBenchmarksForm = (grid?: BenchmarkGrid): BenchmarksForm => {
  if (!grid) {
    return {
      name: '',
      type: 'rep',
      repCount: 0,
      coreExerciseIds: [],
      metricIds: [],
      updateType: 'none',
    };
  }
  return {
    name: grid.name,
    type: grid.type,
    repCount: grid.repCount,
    updateType: grid.updateType,
    coreExerciseIds: grid.coreExercises.map(
      (exercise) => exercise.libraryExerciseId,
    ),
    // KPI grids preload their current metrics asynchronously once the
    // eligibility queries resolve (see the metrics-init effect above).
    metricIds: [],
  };
};
